/**
 * scope-resolver.ts
 *
 * Shared factory for ScopeResolver — resolves SessionScope from sessionId + workspaceId
 * via two sequential API calls at session open time.
 *
 * Closed over apiUrl + apiKey at the entrypoint boundary — zero knowledge of env or fs.
 */

import { assertHttpOk } from "./http-errors";
import type { ScopeResolver } from "./agent";

/**
 * Returns a ScopeResolver that fetches:
 *   1. GET /api/thread?threadId={sessionId}       → conversationId
 *   2. GET /api/workspace/{workspaceId}/hierarchy  → tenant, org
 *
 * Both calls use the same API key. Throws on any HTTP error — fail-fast.
 */
export function makeScopeResolver(
  apiUrl: string,
  authHeaders: Record<string, string>,
  fetchOptions?: RequestInit,
): ScopeResolver {
  return async (sessionId, workspaceId) => {
    const headers = { ...authHeaders };

    const threadUrl = `${apiUrl}/api/thread?threadId=${sessionId}`;
    const threadRes = await fetch(threadUrl, { headers, ...fetchOptions });
    await assertHttpOk(threadRes, threadUrl);
    const thread = (await threadRes.json()) as { conversationId: string };

    const hierarchyUrl = `${apiUrl}/api/workspace/${workspaceId}/hierarchy`;
    const hierarchyRes = await fetch(hierarchyUrl, {
      headers,
      ...fetchOptions,
    });
    await assertHttpOk(hierarchyRes, hierarchyUrl);
    const hierarchy = (await hierarchyRes.json()) as {
      accountId: string;
      organizationId: string;
      workspaceId: string;
    };

    return {
      tenant: hierarchy.accountId,
      org: hierarchy.organizationId,
      workspaceId: hierarchy.workspaceId,
      conversationId: thread.conversationId,
    };
  };
}
